import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Plus, User, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tooth condition colors
const CONDITION_COLORS = {
  healthy: '#22c55e',
  decay: '#ef4444',
  filling: '#3b82f6',
  crown: '#f59e0b',
  missing: '#94a3b8',
  implant: '#8b5cf6',
  extraction: '#dc2626',
  rct: '#ec4899',
  veneer: '#06b6d4',
  bridge: '#f97316',
  sealant: '#84cc16'
};

const CONDITION_LABELS = {
  healthy: 'Healthy',
  decay: 'Decay/Caries',
  filling: 'Filling',
  crown: 'Crown',
  missing: 'Missing',
  implant: 'Implant',
  extraction: 'Needs Extraction',
  rct: 'Root Canal',
  veneer: 'Veneer',
  bridge: 'Bridge',
  sealant: 'Sealant'
};

const SURFACES = ['M', 'O', 'D', 'B', 'L'];
const MATERIALS = ['Amalgam', 'Composite', 'Gold', 'Porcelain', 'Zirconia', 'E-max'];

// Adult tooth positions (1-32)
const UPPER_TEETH = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const LOWER_TEETH = [32, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17];

// SVG Tooth Component
const ToothSVG = ({ number, conditions, surfaces, isSelected, onClick, onSurfaceClick }) => {
  const hasCondition = conditions && conditions.length > 0;
  const mainCondition = hasCondition ? conditions[0]?.entry_type : 'healthy';
  const isMissing = mainCondition === 'missing';
  const baseColor = CONDITION_COLORS[mainCondition] || CONDITION_COLORS.healthy;
  
  // Surface positions for the 5-surface view
  const surfacePositions = {
    M: { x: 5, y: 20, w: 10, h: 20 },   // Mesial (left)
    D: { x: 45, y: 20, w: 10, h: 20 },  // Distal (right)
    B: { x: 15, y: 5, w: 30, h: 15 },   // Buccal/Facial (top for upper, bottom for lower)
    L: { x: 15, y: 40, w: 30, h: 15 },  // Lingual (opposite of buccal)
    O: { x: 15, y: 20, w: 30, h: 20 }   // Occlusal (center)
  };

  const getSurfaceColor = (surface) => {
    const surfaceCondition = surfaces?.find(s => s.surfaces?.includes(surface));
    if (surfaceCondition) {
      return CONDITION_COLORS[surfaceCondition.entry_type] || CONDITION_COLORS.filling;
    }
    return '#e2e8f0'; // Default gray
  };

  return (
    <div className={`relative cursor-pointer transition-transform hover:scale-110 ${isSelected ? 'ring-2 ring-teal-500 rounded' : ''}`}>
      <svg 
        width="60" 
        height="70" 
        viewBox="0 0 60 70" 
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className={isMissing ? 'opacity-30' : ''}
      >
        {/* Tooth outline */}
        <rect 
          x="5" y="5" width="50" height="50" 
          rx="8" 
          fill={isMissing ? '#cbd5e1' : '#f8fafc'} 
          stroke={isSelected ? '#14b8a6' : '#94a3b8'} 
          strokeWidth={isSelected ? 2 : 1}
        />
        
        {/* 5-surface view */}
        {Object.entries(surfacePositions).map(([surface, pos]) => (
          <rect
            key={surface}
            x={pos.x}
            y={pos.y}
            width={pos.w}
            height={pos.h}
            fill={getSurfaceColor(surface)}
            stroke="#94a3b8"
            strokeWidth="0.5"
            rx="2"
            className="cursor-pointer hover:opacity-80"
            onClick={(e) => { e.stopPropagation(); onSurfaceClick(surface); }}
          />
        ))}
        
        {/* Condition indicator dot */}
        {hasCondition && !isMissing && (
          <circle cx="50" cy="10" r="6" fill={baseColor} stroke="#fff" strokeWidth="1" />
        )}
        
        {/* Tooth number */}
        <text x="30" y="65" textAnchor="middle" fontSize="10" fill="#64748b" fontWeight="500">
          {number}
        </text>
      </svg>
    </div>
  );
};

const DentalCharting = () => {
  const { token, user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [chartEntries, setChartEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [selectedSurface, setSelectedSurface] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [formData, setFormData] = useState({
    tooth_number: '',
    entry_type: 'healthy',
    surfaces: [],
    material: '',
    notes: '',
    date_performed: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchChartData();
    }
  }, [selectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dental/chart/${selectedPatient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartEntries(response.data);
    } catch (error) {
      console.error('Chart fetch error:', error);
      setChartEntries([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const entryData = {
        patient_id: selectedPatient.id,
        tooth_number: parseInt(formData.tooth_number),
        entry_type: formData.entry_type,
        surfaces: formData.surfaces.length > 0 ? formData.surfaces : null,
        material: formData.material || null,
        notes: formData.notes || null,
        provider_id: user.id,
        date_performed: formData.date_performed
      };

      await axios.post(`${API_URL}/api/dental/chart`, entryData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Chart entry added');
      setShowModal(false);
      fetchChartData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add chart entry');
    }
  };

  const resetForm = () => {
    setFormData({
      tooth_number: '',
      entry_type: 'healthy',
      surfaces: [],
      material: '',
      notes: '',
      date_performed: new Date().toISOString().split('T')[0]
    });
    setSelectedTooth(null);
    setSelectedSurface(null);
  };

  const handleToothClick = (toothNum) => {
    setSelectedTooth(toothNum);
    setFormData(prev => ({ ...prev, tooth_number: toothNum.toString() }));
    setShowModal(true);
  };

  const handleSurfaceClick = (toothNum, surface) => {
    setSelectedTooth(toothNum);
    setSelectedSurface(surface);
    setFormData(prev => ({ 
      ...prev, 
      tooth_number: toothNum.toString(),
      surfaces: prev.surfaces.includes(surface) 
        ? prev.surfaces.filter(s => s !== surface)
        : [...prev.surfaces, surface]
    }));
    setShowModal(true);
  };

  const toggleSurface = (surface) => {
    setFormData(prev => ({
      ...prev,
      surfaces: prev.surfaces.includes(surface)
        ? prev.surfaces.filter(s => s !== surface)
        : [...prev.surfaces, surface]
    }));
  };

  const getToothConditions = (toothNum) => {
    return chartEntries.filter(e => e.tooth_number === toothNum);
  };

  const getToothSurfaces = (toothNum) => {
    return chartEntries.filter(e => e.tooth_number === toothNum && e.surfaces);
  };

  const filteredPatients = patients.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="dental-charting">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Dental Charting</h1>
            <p className="text-slate-500">Interactive tooth chart and condition tracking</p>
          </div>
          {selectedPatient && (
            <Button 
              variant="outline" 
              onClick={() => setShowHistoryModal(true)}
              className="flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              View History
            </Button>
          )}
        </div>

        {/* Patient Selection */}
        {!selectedPatient ? (
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Select Patient</h2>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {filteredPatients.map(patient => (
                <div
                  key={patient.id}
                  className="p-3 border rounded-lg cursor-pointer hover:bg-teal-50 hover:border-teal-300 transition-colors"
                  onClick={() => setSelectedPatient(patient)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-medium">
                      {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium">{patient.last_name}, {patient.first_name}</div>
                      <div className="text-sm text-slate-500">{patient.phone}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Patient Header */}
            <div className="card p-4 mb-6 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-medium text-lg">
                  {selectedPatient.first_name?.charAt(0)}{selectedPatient.last_name?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{selectedPatient.last_name}, {selectedPatient.first_name}</h2>
                  <p className="text-sm text-slate-500">
                    DOB: {selectedPatient.date_of_birth} | Phone: {selectedPatient.phone}
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => { setSelectedPatient(null); setChartEntries([]); }}>
                Change Patient
              </Button>
            </div>

            {/* Legend */}
            <div className="card p-4 mb-6">
              <h3 className="font-medium mb-3">Condition Legend</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(CONDITION_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    <div 
                      className="w-4 h-4 rounded" 
                      style={{ backgroundColor: CONDITION_COLORS[key] }}
                    />
                    <span className="text-slate-600">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Dental Chart */}
            <div className="card p-6">
              <h3 className="font-semibold mb-4 text-center">Adult Dentition Chart</h3>
              
              {/* Upper Teeth */}
              <div className="mb-2">
                <div className="text-xs text-slate-500 text-center mb-1">Upper Right → Upper Left</div>
                <div className="flex justify-center gap-1">
                  {UPPER_TEETH.map(num => (
                    <ToothSVG
                      key={num}
                      number={num}
                      conditions={getToothConditions(num)}
                      surfaces={getToothSurfaces(num)}
                      isSelected={selectedTooth === num}
                      onClick={() => handleToothClick(num)}
                      onSurfaceClick={(surface) => handleSurfaceClick(num, surface)}
                    />
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t-2 border-dashed border-slate-300 my-4" />

              {/* Lower Teeth */}
              <div>
                <div className="flex justify-center gap-1">
                  {LOWER_TEETH.map(num => (
                    <ToothSVG
                      key={num}
                      number={num}
                      conditions={getToothConditions(num)}
                      surfaces={getToothSurfaces(num)}
                      isSelected={selectedTooth === num}
                      onClick={() => handleToothClick(num)}
                      onSurfaceClick={(surface) => handleSurfaceClick(num, surface)}
                    />
                  ))}
                </div>
                <div className="text-xs text-slate-500 text-center mt-1">Lower Right → Lower Left</div>
              </div>

              <p className="text-center text-sm text-slate-400 mt-4">
                Click on a tooth or surface to add/edit conditions
              </p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="card p-4 text-center">
                <div className="text-2xl font-bold text-slate-800">
                  {32 - chartEntries.filter(e => e.entry_type === 'missing').length}
                </div>
                <div className="text-sm text-slate-500">Teeth Present</div>
              </div>
              <div className="card p-4 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {chartEntries.filter(e => e.entry_type === 'decay').length}
                </div>
                <div className="text-sm text-slate-500">Active Decay</div>
              </div>
              <div className="card p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {chartEntries.filter(e => e.entry_type === 'filling').length}
                </div>
                <div className="text-sm text-slate-500">Restorations</div>
              </div>
              <div className="card p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">
                  {chartEntries.filter(e => e.entry_type === 'crown').length}
                </div>
                <div className="text-sm text-slate-500">Crowns</div>
              </div>
            </div>
          </>
        )}

        {/* Add Entry Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Add Chart Entry - Tooth #{formData.tooth_number}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Condition Type *</Label>
                <Select 
                  value={formData.entry_type} 
                  onValueChange={(v) => setFormData({...formData, entry_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONDITION_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: CONDITION_COLORS[key] }} />
                          {label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Surfaces (click to toggle)</Label>
                <div className="flex gap-2 mt-2">
                  {SURFACES.map(surface => (
                    <Button
                      key={surface}
                      type="button"
                      size="sm"
                      variant={formData.surfaces.includes(surface) ? 'default' : 'outline'}
                      onClick={() => toggleSurface(surface)}
                      className="w-10"
                    >
                      {surface}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">M=Mesial, O=Occlusal, D=Distal, B=Buccal, L=Lingual</p>
              </div>

              {['filling', 'crown', 'veneer', 'implant'].includes(formData.entry_type) && (
                <div>
                  <Label>Material</Label>
                  <Select 
                    value={formData.material} 
                    onValueChange={(v) => setFormData({...formData, material: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {MATERIALS.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Date Performed</Label>
                <Input
                  type="date"
                  value={formData.date_performed}
                  onChange={(e) => setFormData({...formData, date_performed: e.target.value})}
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">
                  Add Entry
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* History Modal */}
        <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Chart History</DialogTitle>
            </DialogHeader>
            {chartEntries.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No chart entries recorded</p>
            ) : (
              <div className="space-y-3">
                {chartEntries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(entry => (
                  <div key={entry.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: CONDITION_COLORS[entry.entry_type] }}
                        >
                          {entry.tooth_number}
                        </div>
                        <div>
                          <div className="font-medium">{CONDITION_LABELS[entry.entry_type]}</div>
                          <div className="text-sm text-slate-500">
                            {entry.surfaces?.join(', ') || 'Full tooth'}
                            {entry.material && ` • ${entry.material}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    {entry.notes && (
                      <p className="text-sm text-slate-600 mt-2 ml-11">{entry.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DentalCharting;
